import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  checkoutGuestSettings,
  currentGuestRows,
  guestsForPayment,
} from '../lib/guests/checkoutGuests';
import getFormFieldValues from '../lib/getFormFieldValues';
import useCheckoutState, { checkoutStates } from './useCheckoutState';
import { checkoutTypes } from '../enums/checkout-types';
import useEventTracking from './useEventTracking';
import api from '../api';
import getBillingDetailsFromFields, {
  getShippingDetailsFromFields,
} from '../lib/getBillingDetailsFromFields';
import getSessionId from '../lib/getSessionId';
import useAfterPaymentActions from './useAfterPaymentActions';
import logException from '../services/logException';
import storage from '../services/storage';
import getFormVariantValues from '../lib/getFormVariantValues';
import getCheckoutPath from '../lib/getCheckoutPath';
import getSellerSlug from '../services/getSellerSlug';
import getCheckoutSlug from '../services/getCheckoutSlug';
import getTicketTypeValues from '../lib/getTicketTypeValues';
import getTicketPwywAmountValues from '../lib/getTicketPwywAmountValues';
import getOrderBumpValues from '../lib/getOrderBumpValues';
import checkIsCoverProcessingFeeSelected from '../lib/checkCoverProcessingFee';
import getAuthToken from '../lib/getAuthToken';
import useCheckoutRouter from './useCheckoutRouter';
import useRewardful from './useRewardful';
import useTolt from './useTolt';
import useRecaptcha from './useRecaptcha';

export const TEST_CARD_LIVE_ERROR = 'Test card payments only work on pages in test mode.';

// The tile is a card, so fees and records follow card even when another method was selected.
const TEST_CARD_REQUEST_FIELDS = Object.freeze({ testCard: true, selectedPaymentMethod: 'card' });

const SUBSCRIPTION_FULFILLED_STATUSES = Object.freeze([
  'trialing',
  'paused',
  'scheduled',
  'active',
  'past_due',
  'canceled',
  'unpaid',
  'completed',
]);

function useCheckoutHelpers() {
  const {
    form,
    address,
    coupon,
    dynamicPrice,
    dynamicPlanIterations,
    payWhatYouWantPrice,
    checkout,
    page,
    product,
    seller,
    visitId,
    query,
    upsell,
    isUpsell,
    isUpsellRetry,
    setIsUpsellRetry,
    isTestingPayments,
    setFormError,
    setState,
    payment,
    setPayment,
    paymentMethod,
    stripePaymentMethodId,
    setStripePaymentMethodId,
    setSubmission,
    checkoutType,
    selectedPaymentMethod,
    paymentOption,
    selectedPriceId,
    effectiveSelectedPriceId,
  } = useCheckoutState();

  // Only dashboard test payments need to prove ownership; a flagged page needs no
  // credential because the flag decides on its own.
  const ownerToken = isTestingPayments ? getAuthToken() : null;
  const { handleAfterPaymentActions } = useAfterPaymentActions();
  const { trackPurchaseEvent, trackSubmissionEvent } = useEventTracking();
  const stripe = useStripe();
  const elements = useElements();
  const router = useCheckoutRouter();
  const { getRewardfulReferralId } = useRewardful();
  const { getToltReferralId } = useTolt();
  const { executeRecaptcha } = useRecaptcha();

  function getSubscriptionData({ recaptchaToken, stripePaymentMethodEvent }) {
    const { fields, name, email } = getFormFieldValues(
      form.fields,
      page.fields,
      stripePaymentMethodEvent,
    );

    const variants = getFormVariantValues(
      product.variants,
      form.variants,
      undefined,
      effectiveSelectedPriceId,
    );

    const url = new URL(window.location.href);

    const checkoutEventId = url.searchParams.get('checkoutEventId');

    const rewardfulReferral = getRewardfulReferralId();
    const toltReferral = getToltReferralId();

    const orderBumps = getOrderBumpValues(
      form.orderBumps,
      form.orderBumpAmounts,
      page.eventDetails?.currency || product?.currency,
    );

    return {
      recaptchaToken,
      ...(payment && {
        subscriptionId: payment._id,
      }),
      ...(checkoutEventId && { checkoutEventId }),
      pageId: checkout._id,
      fields,
      name,
      email,
      address,
      variants: variants,
      sellerId: seller._id,
      ...(isTestingPayments && { testingPayments: true }),
      queryParameters: query,
      ...(dynamicPrice && { dynamicPrice }),
      ...(dynamicPlanIterations && { dynamicPlanIterations }),
      ...(isUpsell && {
        upsell: true,
        upsellRetry: isUpsellRetry,
        upsellPayment: upsell?.initialPaymentId,
        upsellSubscription: upsell?.initialSubscriptionId,
        upsellCheckout: upsell?.initialCheckoutId,
        upsellCustomer: upsell?.customerId,
      }),
      session: getSessionId(),
      visit: visitId,
      couponId: coupon?._id || null,
      ...(payWhatYouWantPrice && { payWhatYouWantPrice }),
      ...(rewardfulReferral && { rewardfulReferral }),
      ...(toltReferral && { toltReferral }),
      selectedPaymentMethod: selectedPaymentMethod,
      paymentOption,
      ...(selectedPriceId && { priceId: selectedPriceId }),
      ...(orderBumps.length > 0 && { orderBumps }),
      coverProcessingFee: checkIsCoverProcessingFeeSelected({ page, form, paymentOption }),
    };
  }

  async function createSubscription({ recaptchaToken, stripePaymentMethodEvent, testCard } = {}) {
    setState(checkoutStates.CREATING_SUBSCRIPTION);

    const subscriptionData = getSubscriptionData({ recaptchaToken, stripePaymentMethodEvent });
    if (testCard) Object.assign(subscriptionData, TEST_CARD_REQUEST_FIELDS);

    // For non-upsells OR upsell retries, we need to initialize the subscription
    // and collect payment method via setup intent.
    // Upsell retries need this because the original payment method failed.
    if (!isUpsell || isUpsellRetry) {
      const {
        data: initializedSubscriptionData,
        error,
        message,
      } = await api.initializeSubscription(subscriptionData, ownerToken);

      if (!initializedSubscriptionData || error) {
        throw new Error(error || message || 'Unable to create subscription');
      }

      /**
       * The server refused to start a new payment because Stripe already processed this
       * subscription (e.g. a retry after an interrupted attempt) and synced the local
       * record with Stripe. The response carries no client_secret, so attempting another
       * Stripe confirmation would fail — complete the checkout if it's now fulfilled.
       *
       * This must run BEFORE the generic fulfilled-status check below: the healed
       * subscription usually arrives with a fulfilled status, and the generic check
       * returns silently (leaving the UI in a processing state) on the assumption
       * that another in-flight submit will finish the flow. On a retry there is no
       * other in-flight submit — this response IS the outcome, so complete it here.
       */
      if (initializedSubscriptionData.already_processed) {
        const processedSubscription = initializedSubscriptionData.subscription;

        if (
          processedSubscription &&
          SUBSCRIPTION_FULFILLED_STATUSES.includes(processedSubscription.status)
        ) {
          await completePayment(processedSubscription);
          return;
        }

        // The server refused to re-charge but the local record could not be
        // confirmed as fulfilled (e.g. Stripe reports paid while the setup never
        // completed). Surface an honest, actionable message instead of retrying.
        throw new Error(
          'Your payment has already been processed. Please check your email for your order confirmation.',
        );
      }

      /**
       * This is a defensive check to prevent subscription payment from being attempted twice when a user manages to submit the form twice.
       * If the subscription is already fulfilled, skip and let the original flow complete the subscription.
       */
      if (
        initializedSubscriptionData.subscription &&
        SUBSCRIPTION_FULFILLED_STATUSES.includes(initializedSubscriptionData.subscription.status)
      ) {
        return;
      }

      if (testCard && initializedSubscriptionData.subscription?.livemode !== false) {
        throw new Error(TEST_CARD_LIVE_ERROR);
      }

      await setPayment(initializedSubscriptionData.subscription);

      if (initializedSubscriptionData.subscription._id) {
        subscriptionData.subscriptionId = initializedSubscriptionData.subscription._id;
      }

      const billingDetails = getBillingDetailsFromFields(
        getFormFieldValues(form.fields, page.fields).fields,
      );

      // The API opens no Stripe intent for a test subscription, so there is nothing to confirm.
      // checkPaymentDetails has already sent the card to Stripe's test key, which rejects a real card.
      const isTestSubscription = initializedSubscriptionData.subscription?.livemode === false;

      try {
        if (!isTestSubscription) {
          const isPaymentIntentFlow = initializedSubscriptionData.intent_type === 'payment_intent';

          if (
            paymentMethod === 'multiple' ||
            stripePaymentMethodEvent?.elementType === 'expressCheckout'
          ) {
            let returnUrl = window.location.href;

            // Handle popup checkout redirect
            if (checkoutType === checkoutTypes.POPUP) {
              const location = origin;
              const checkoutSlug = getCheckoutSlug(checkout);
              const sellerSlug = getSellerSlug(seller);
              const returnPath = getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.CHECKOUT);
              returnUrl = location + returnPath;
            }

            if (isPaymentIntentFlow) {
              const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                clientSecret: initializedSubscriptionData.client_secret,
                confirmParams: {
                  return_url: returnUrl,
                  payment_method_data: {
                    billing_details: {
                      ...(billingDetails.email && { email: billingDetails.email }),
                      ...(billingDetails.name && { name: billingDetails.name }),
                      ...(billingDetails.phone && { phone: billingDetails.phone }),
                      ...(billingDetails.address && { address: billingDetails.address }),
                    },
                  },
                },
                redirect: 'if_required',
              });

              if (error) throw new Error(error.message);
              subscriptionData.payment_method_id = paymentIntent.payment_method;
              subscriptionData.payment_intent_id = paymentIntent.id;
            } else {
              const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                clientSecret: initializedSubscriptionData.client_secret,
                confirmParams: {
                  return_url: returnUrl,
                  payment_method_data: {
                    billing_details: {
                      ...(billingDetails.email && { email: billingDetails.email }),
                      ...(billingDetails.name && { name: billingDetails.name }),
                      ...(billingDetails.phone && { phone: billingDetails.phone }),
                      ...(billingDetails.address && { address: billingDetails.address }),
                    },
                  },
                },
                redirect: 'if_required',
              });

              if (error) throw new Error(error.message);
              subscriptionData.payment_method_id = setupIntent.payment_method;
              subscriptionData.setup_intent_id = setupIntent.id;
            }
          } else {
            // Traditional card element flow
            const paymentMethodConfig = stripePaymentMethodId
              ? stripePaymentMethodId
              : {
                  card: elements.getElement(CardElement),
                  billing_details: billingDetails,
                };

            if (isPaymentIntentFlow) {
              const { error, paymentIntent } = await stripe.confirmCardPayment(
                initializedSubscriptionData.client_secret,
                { payment_method: paymentMethodConfig },
              );

              if (error) throw new Error(error.message);
              subscriptionData.payment_method_id = paymentIntent.payment_method;
              subscriptionData.payment_intent_id = paymentIntent.id;
            } else {
              const { error, setupIntent } = await stripe.confirmCardSetup(
                initializedSubscriptionData.client_secret,
                { payment_method: paymentMethodConfig },
              );

              if (error) throw new Error(error.message);
              subscriptionData.payment_method_id = setupIntent.payment_method;
              subscriptionData.setup_intent_id = setupIntent.id;
            }
          }
        }
      } catch (error) {
        logException(error.message);
        setFormError(error.message);
        setState(checkoutStates.INITIAL);

        if (stripePaymentMethodId) {
          setStripePaymentMethodId(null);
        }

        if (subscriptionData.subscriptionId) {
          await api.setSubscriptionError(error.message, subscriptionData.subscriptionId);
        }

        throw error;
      }
    }

    // The original token was already consumed by initializeSubscription
    const freshRecaptchaToken = await executeRecaptcha('checkout');
    if (freshRecaptchaToken) {
      subscriptionData.recaptchaToken = freshRecaptchaToken;
    }

    const subscription = await api.confirmSubscription(subscriptionData, ownerToken);

    if (!subscription.data) {
      const errorMessage = subscription.error || subscription.message || 'Subscription failed';
      if (subscriptionData.subscriptionId) {
        await api.setSubscriptionError(errorMessage, subscriptionData.subscriptionId);
      }
      if (isUpsell && !isUpsellRetry) {
        setIsUpsellRetry(true);
        throw new Error('Authentication failed, please re-enter your card details');
      }
      throw new Error(errorMessage);
    }

    await setPayment(subscription.data);

    if (subscription.error || subscription.message) {
      const errorMessage = subscription.error || subscription.message || 'Subscription failed';
      if (subscription.data._id) {
        await api.setSubscriptionError(errorMessage, subscription.data._id);
      }
      if (isUpsell && !isUpsellRetry) {
        setIsUpsellRetry(true);
        throw new Error('Authentication failed, please re-enter your card details');
      }
      throw new Error(errorMessage);
    }

    const isIncomplete = subscription.data.status === 'incomplete';
    const hasIncompletePayment = subscription.data.stripePaymentIntentClientSecret;
    const hasPendingSetupIntent = subscription.data.stripePendingSetupIntent;
    const paymentStatus = subscription.data.paymentStatus;

    // Only call handleSubscriptionPayment if the PaymentIntent requires frontend action (3DS).
    // If paymentStatus is 'processing' or 'succeeded', the backend's off_session attempt
    // already handled it - calling confirm again here causes a lock_timeout error.
    const needsFrontendConfirmation =
      paymentStatus === 'requires_action' || paymentStatus === 'requires_confirmation';

    if (isIncomplete && hasIncompletePayment && needsFrontendConfirmation) {
      // Non-trial subscription: confirm the payment intent created by Stripe
      await handleSubscriptionPayment(subscription.data, { stripePaymentMethodEvent });
    } else if (isIncomplete && hasIncompletePayment) {
      // Payment is processing/succeeded server-side, poll for completion
      await pollSubscriptionStatus(subscription.data);
    } else if (isIncomplete && hasPendingSetupIntent) {
      // India cards / mandate confirmation: poll until webhooks update status
      await pollSubscriptionStatus(subscription.data);
    } else {
      // Trial subscription or already active: complete immediately
      await completePayment(subscription.data);
    }
  }

  async function createSubmission({ recaptchaToken } = {}) {
    setState(checkoutStates.CREATING_SUBMISSION);

    const { fields, name, email } = getFormFieldValues(form.fields, page.fields);

    const submissionData = {
      recaptchaToken,
      pageId: checkout._id,
      fields,
      name,
      email,
      address,
      sellerId: seller._id,
      ...(isTestingPayments && { testingPayments: true }),
      queryParameters: query,
      session: getSessionId(),
      visit: visitId,
    };

    const submission = await api.createSubmission(checkout._id, submissionData, ownerToken);

    if (!submission.data) {
      throw new Error(submission.error || submission.message || 'Submission failed');
    }

    await completeSubmission(submission.data);
  }

  function getPaymentData({ recaptchaToken, stripePaymentMethodEvent }) {
    const { fields, name, email } = getFormFieldValues(
      form.fields,
      page.fields,
      stripePaymentMethodEvent,
    );

    const variants = getFormVariantValues(
      product.variants,
      form.variants,
      // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed (product.currency)
      product.currency,
      effectiveSelectedPriceId,
    );

    const tickets = getTicketTypeValues(checkout.ticketGroups, form.ticketGroups);

    const ticketPwywAmounts = getTicketPwywAmountValues(
      checkout.ticketGroups,
      form.ticketGroups,
      page.eventDetails?.currency || product?.currency,
    );

    const url = new URL(window.location.href);

    const checkoutEventId = url.searchParams.get('checkoutEventId');

    const rewardfulReferral = getRewardfulReferralId();
    const toltReferral = getToltReferralId();

    const orderBumps = getOrderBumpValues(
      form.orderBumps,
      form.orderBumpAmounts,
      page.eventDetails?.currency || product?.currency,
    );

    return {
      recaptchaToken,
      ...(payment && {
        paymentId: payment._id,
        paymentIntentId: payment.paymentIntent?.id,
      }),
      ...(checkoutEventId && { checkoutEventId }),
      pageId: checkout._id,
      fields,
      name,
      email,
      address,
      variants,
      tickets,
      ticketPwywAmounts,
      sellerId: seller._id,
      ...(isTestingPayments && { testingPayments: true }),
      queryParameters: query,
      session: getSessionId(),
      visit: visitId,
      ...(isUpsell && {
        upsell: true,
        upsellRetry: isUpsellRetry,
        upsellPayment: upsell?.initialPaymentId,
        upsellSubscription: upsell?.initialSubscriptionId,
        upsellCheckout: upsell?.initialCheckoutId,
        upsellCustomer: upsell?.customerId,
      }),
      couponId: coupon?._id || null,
      ...(payWhatYouWantPrice && { payWhatYouWantPrice }),
      ...(dynamicPrice && { dynamicPrice }),
      ...(rewardfulReferral && { rewardfulReferral }),
      ...(toltReferral && { toltReferral }),
      selectedPaymentMethod: selectedPaymentMethod,
      paymentOption: paymentOption,
      ...(selectedPriceId && { priceId: selectedPriceId }),
      ...(orderBumps.length > 0 && { orderBumps }),
      coverProcessingFee: checkIsCoverProcessingFeeSelected({ page, form, paymentOption }),
      ...(checkoutGuestSettings(checkout) &&
        !isUpsell && { guests: guestsForPayment(currentGuestRows(checkout, form)) }),
    };
  }

  async function createPayment({ recaptchaToken, stripePaymentMethodEvent, testCard } = {}) {
    setState(checkoutStates.CREATING_PAYMENT);

    const paymentData = getPaymentData({ recaptchaToken, stripePaymentMethodEvent });
    if (testCard) Object.assign(paymentData, TEST_CARD_REQUEST_FIELDS);

    // TODO migrate to useMutation
    const payment = await api.createPayment(checkout._id, paymentData, ownerToken);

    if (!payment.data || payment.data.error) {
      if (isUpsell) {
        setIsUpsellRetry(true);
      }

      if (payment.data?._id) {
        setPayment(payment.data);
      }

      const error = payment.data?.error || payment.error || payment.message || 'Payment failed';
      throw new Error(error);
    }

    // The server decides test mode. A live answer here means the button showed on a page that
    // takes real money, so stop before Stripe is asked to confirm without a card.
    if (testCard && payment.data.livemode !== false) {
      throw new Error(TEST_CARD_LIVE_ERROR);
    }

    await processPayment(payment.data, { stripePaymentMethodEvent });
  }

  async function pollPaymentStatus(payment) {
    const pollPayment = setInterval(handlePollPaymentStatus, 5000);

    let pollCount = 0;

    async function handlePollPaymentStatus() {
      pollCount = pollCount + 1;

      const response = await api.getPaymentStatus(payment._id, getSessionId());

      const chargeStatus = response?.data?.status;
      if (chargeStatus === 'created' && pollCount < 12) {
        return;
      }

      // merge payment with response data to add status and files
      completePayment({ ...payment, ...response.data });
      clearInterval(pollPayment);
    }

    handlePollPaymentStatus();
  }

  async function pollSubscriptionStatus(subscription) {
    const pollPayment = setInterval(handlePollSubscriptionStatus, 5000);

    let pollCount = 0;

    async function handlePollSubscriptionStatus() {
      pollCount = pollCount + 1;

      const response = await api.getSubscriptionStatus(subscription._id, getSessionId());
      const subscriptionStatus = response?.data?.status;
      const paymentStatus = response?.data?.paymentStatus;
      const paymentError = response?.data?.paymentError;

      const isSuccess =
        subscriptionStatus === 'active' ||
        subscriptionStatus === 'trialing' ||
        subscriptionStatus === 'scheduled';

      // Success wins over any stale paymentError left on the subscription
      if (isSuccess) {
        completePayment({ ...subscription, ...response.data });
        clearInterval(pollPayment);
        return;
      }

      // Check for failed status - show error immediately
      if (subscriptionStatus === 'failed') {
        clearInterval(pollPayment);
        const errorMessage = paymentError || 'Payment failed. Please try again.';
        logException(errorMessage);
        setFormError(errorMessage);
        setState(checkoutStates.INITIAL);
        return;
      }

      const isTransient =
        subscriptionStatus === 'created' ||
        subscriptionStatus === 'incomplete' ||
        paymentStatus === 'processing' ||
        paymentStatus === 'requires_payment_method';

      // Keep polling while the current retry is still in flight, even if an old
      // paymentError is still attached to the subscription record.
      if (isTransient && pollCount < 12) {
        return;
      }

      // Once the retry is no longer transient, a populated paymentError is terminal.
      if (paymentError) {
        clearInterval(pollPayment);
        logException(paymentError);
        setFormError(paymentError);
        setState(checkoutStates.INITIAL);
        return;
      }

      if (!isSuccess) {
        clearInterval(pollPayment);
        const errorMessage = paymentError || 'Payment could not be completed. Please try again.';
        logException(errorMessage);
        setFormError(errorMessage);
        setState(checkoutStates.INITIAL);
        return;
      }
    }

    handlePollSubscriptionStatus();
  }

  async function completeSubmission(submission) {
    trackSubmissionEvent(submission);

    await setSubmission(submission);

    await setState(checkoutStates.COMPLETED);

    await handleAfterPaymentActions(submission);
  }

  async function completePayment(payment) {
    trackPurchaseEvent(payment);

    await setPayment(payment);

    await setState(checkoutStates.COMPLETED);
    storage.session.removeItem('CheckoutPage.pid');

    await handleAfterPaymentActions(payment);
  }

  // Retries a Stripe API call if it fails due to a lock_timeout error.
  // This can happen when the backend just updated the subscription/invoice
  // and Stripe still has an internal lock on the payment intent.
  async function retryOnStripeLock(fn, { maxRetries = 3, initialDelayMs = 500 } = {}) {
    let lastResult;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      lastResult = await fn();

      // Check if we got a lock_timeout error
      if (lastResult.error?.code === 'lock_timeout' && attempt < maxRetries) {
        const delayMs = initialDelayMs * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delayMs));
        continue;
      }

      // Return on success or any other error
      return lastResult;
    }

    return lastResult;
  }

  // Server-side draft validation can strip an incomplete address field from the
  // round-tripped fields while the Payment Element keeps its 'never' billing
  // fields config; confirm must then supply the address from the live form or
  // Stripe.js rejects the call.
  function getConfirmationDetails(roundTrippedFields) {
    let billingDetails = getBillingDetailsFromFields(roundTrippedFields);
    let shippingDetails = getShippingDetailsFromFields(roundTrippedFields);

    if (billingDetails.address && shippingDetails.address) {
      return { billingDetails, shippingDetails };
    }

    const liveFields = getFormFieldValues(form.fields, page.fields).fields;

    if (!billingDetails.address) {
      const liveAddress = getBillingDetailsFromFields(liveFields).address;
      if (liveAddress) {
        billingDetails = { ...billingDetails, address: liveAddress };
      }
    }

    if (!shippingDetails.address) {
      const liveShipping = getShippingDetailsFromFields(liveFields);
      if (liveShipping.address) {
        shippingDetails = {
          ...shippingDetails,
          name: shippingDetails.name || liveShipping.name,
          address: liveShipping.address,
        };
      }
    }

    return { billingDetails, shippingDetails };
  }

  async function handleSubscriptionPayment(subscription, { stripePaymentMethodEvent } = {}) {
    const clientSecret = subscription.stripePaymentIntentClientSecret;
    const isExpressCheckout = stripePaymentMethodEvent?.elementType === 'expressCheckout';

    const { billingDetails, shippingDetails } = getConfirmationDetails(subscription.fields);

    try {
      if (paymentMethod === 'multiple' || isExpressCheckout) {
        const hasShipping = !!shippingDetails.address && !!shippingDetails.name;

        let returnUrl = window.location.href;

        // pop-up is rendered on /overlay path
        // so we need to redirect to the hosted checkout page after payment
        if (checkoutType === checkoutTypes.POPUP) {
          // use checkout origin to redirect to the correct domain
          const location = origin;

          const checkoutSlug = getCheckoutSlug(checkout);
          const sellerSlug = getSellerSlug(seller);

          const returnPath = getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.CHECKOUT);

          returnUrl = location + returnPath;
        }

        const { error } = await retryOnStripeLock(() =>
          stripe.confirmPayment({
            // The wallet's confirm event is spent on the setup intent; passing Elements
            // again would confirm through the closed sheet. Use the attached method.
            ...(!isExpressCheckout && { elements }),
            clientSecret: clientSecret,
            confirmParams: {
              return_url: returnUrl,
              ...(!isExpressCheckout && {
                payment_method_data: {
                  billing_details: {
                    ...(billingDetails.email && { email: billingDetails.email }),
                    ...(billingDetails.name && { name: billingDetails.name }),
                    ...(billingDetails.phone && { phone: billingDetails.phone }),
                    ...(billingDetails.address && {
                      address: billingDetails.address,
                    }),
                  },
                },
              }),
              ...(hasShipping && {
                shipping: {
                  name: shippingDetails.name,
                  address: shippingDetails.address,
                },
              }),
            },
            redirect: 'if_required',
          }),
        );

        if (error) {
          throw new Error(error.message);
        }

        return pollSubscriptionStatus(subscription);
      } else {
        // The subscription's payment intent should already have a payment method attached
        // (from setup intent or upsell's existing payment method).
        // First, try to confirm without passing a new payment_method - this avoids double 3DS.
        const { error: confirmError, paymentIntent } = await retryOnStripeLock(() =>
          stripe.confirmCardPayment(clientSecret),
        );

        if (confirmError) {
          // Don't retry if user cancelled/failed authentication - just show the error
          if (confirmError.code === 'payment_intent_authentication_failure') {
            throw new Error(confirmError.message);
          }

          // If confirmation failed because no payment method is attached,
          // we need to collect one
          if (
            confirmError.code === 'payment_intent_unexpected_state' ||
            confirmError.message?.includes('payment method') ||
            paymentIntent?.status === 'requires_payment_method'
          ) {
            const cardElement = elements.getElement(CardElement);

            // For upsells without card element, trigger retry flow
            if (!stripePaymentMethodId && !cardElement && isUpsell && !isUpsellRetry) {
              setIsUpsellRetry(true);
              setFormError('Please enter your card details to complete the payment');
              setState(checkoutStates.INITIAL);
              return;
            }

            // Retry with payment method
            const paymentMethodConfig = stripePaymentMethodId
              ? stripePaymentMethodId
              : {
                  card: cardElement,
                  billing_details: billingDetails,
                };

            const { error: retryError } = await retryOnStripeLock(() =>
              stripe.confirmCardPayment(clientSecret, {
                payment_method: paymentMethodConfig,
              }),
            );

            if (retryError) {
              throw new Error(retryError.message);
            }
          } else {
            throw new Error(confirmError.message);
          }
        }

        return pollSubscriptionStatus(subscription);
      }
    } catch (error) {
      logException(error.message);
      setFormError(error.message);
      setState(checkoutStates.INITIAL);

      if (stripePaymentMethodId) {
        setStripePaymentMethodId(null);
      }

      await api.setSubscriptionError(error.message, subscription._id);
    }
  }

  // process payment
  async function processPayment(payment, { stripePaymentMethodEvent } = {}) {
    await setPayment(payment);

    if (payment.amount === 0 || payment.livemode === false) {
      return completePayment(payment);
    }

    if (payment.status !== 'created') {
      return completePayment(payment);
    }

    // if upsell payment is already succeeded, complete payment
    if (isUpsell && payment.paymentIntent.status === 'succeeded') {
      return pollPaymentStatus(payment);
    }

    if (
      isUpsell &&
      !isUpsellRetry &&
      (payment.paymentIntent.status === 'requires_action' ||
        payment.paymentIntent.status === 'requires_payment_method')
    ) {
      // will show payment method form
      setIsUpsellRetry(true);
      setFormError(payment.paymentIntent.last_payment_error?.message);
      await setState(checkoutStates.INITIAL);
      return;
    }

    try {
      const { billingDetails, shippingDetails } = getConfirmationDetails(payment.fields);

      if (
        paymentMethod === 'multiple' ||
        stripePaymentMethodEvent?.elementType === 'expressCheckout'
      ) {
        const hasShipping = !!shippingDetails.address && !!shippingDetails.name;

        let returnUrl = window.location.href;

        // pop-up is rendered on /overlay path
        // so we need to redirect to the hosted checkout page after payment
        if (checkoutType === checkoutTypes.POPUP) {
          // use checkout origin to redirect to the correct domain
          const location = origin;

          const checkoutSlug = getCheckoutSlug(checkout);
          const sellerSlug = getSellerSlug(seller);

          const returnPath = getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.CHECKOUT);

          returnUrl = location + returnPath;
        }

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret: payment.stripePaymentIntentClientSecret,
          confirmParams: {
            return_url: returnUrl,
            payment_method_data: {
              billing_details: {
                ...(billingDetails.email && { email: billingDetails.email }),
                ...(billingDetails.name && { name: billingDetails.name }),
                ...(billingDetails.phone && { phone: billingDetails.phone }),
                ...(billingDetails.address && {
                  address: billingDetails.address,
                }),
              },
            },
            ...(hasShipping && {
              shipping: {
                name: shippingDetails.name,
                address: shippingDetails.address,
              },
            }),
          },
          redirect: 'if_required',
        });

        if (error) {
          throw new Error(error.message);
        }

        if (
          paymentIntent.status === 'requires_action' ||
          paymentIntent.status === 'requires_payment_method'
        ) {
          setState(checkoutStates.INITIAL);
          return;
        }

        return pollPaymentStatus(payment);
      } else if (stripePaymentMethodId) {
        const { error } = await stripe.confirmCardPayment(payment.stripePaymentIntentClientSecret, {
          payment_method: stripePaymentMethodId,
        });

        if (error) {
          throw new Error(error.message);
        }

        return pollPaymentStatus(payment);
      } else if (paymentMethod === 'card') {
        // For upsells, the payment intent should already have a payment method attached.
        // Try to confirm without passing a new payment_method first to avoid double 3DS.
        if (isUpsell) {
          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
            payment.stripePaymentIntentClientSecret,
          );

          if (confirmError) {
            // If confirmation failed because no payment method is attached, collect one
            if (
              confirmError.code === 'payment_intent_unexpected_state' ||
              confirmError.message?.includes('payment method') ||
              paymentIntent?.status === 'requires_payment_method'
            ) {
              const cardElement = elements.getElement(CardElement);

              if (!cardElement && !isUpsellRetry) {
                setIsUpsellRetry(true);
                setFormError('Please enter your card details to complete the payment');
                setState(checkoutStates.INITIAL);
                return;
              }

              const { error: retryError } = await stripe.confirmCardPayment(
                payment.stripePaymentIntentClientSecret,
                {
                  payment_method: {
                    card: cardElement,
                    billing_details: billingDetails,
                  },
                },
              );

              if (retryError) {
                throw new Error(retryError.message);
              }
            } else {
              throw new Error(confirmError.message);
            }
          }

          return pollPaymentStatus(payment);
        }

        // For non-upsells, use the card element as before
        const cardElement = elements.getElement(CardElement);

        /**
         * The card element is hidden when a checkout is free.
         *
         * A checkout can be made free by a coupon, bulk discount or the product itself being free.
         *
         * If the checkout/coupon/product is updated and the checkout
         * is no longer free we will arrive at this point.
         *
         * We check if the cardElement is null and throw an error to
         * refresh the page to get the latest checkout/coupon/product.
         */
        if (cardElement == null) {
          throw new Error('The page updated. Please refresh the page.');
        }

        const { error } = await stripe.confirmCardPayment(payment.stripePaymentIntentClientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        });

        if (error) {
          throw new Error(error.message);
        }

        return pollPaymentStatus(payment);
      }
    } catch (error) {
      logException(error.message);
      setFormError(error.message);
      setState(checkoutStates.INITIAL);

      if (stripePaymentMethodId) {
        setStripePaymentMethodId(null);
      }

      if (payment?._id) {
        await api.setPaymentError(error.message, payment._id);
      }
    }
  }

  async function processPaymentIntent(paymentIntentClientSecret) {
    setState(checkoutStates.PROCESSING_PAYMENT);

    if (!stripe) return;

    if (!paymentIntentClientSecret) {
      setState(checkoutStates.PENDING);
      return;
    }

    let payment;
    try {
      const { paymentIntent, error } =
        await stripe.retrievePaymentIntent(paymentIntentClientSecret);

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        const { data: payment, error: chargeError } =
          await api.getChargeByClientSecret(paymentIntentClientSecret);

        if (chargeError) {
          throw new Error(chargeError);
        }

        if (!payment) {
          throw new Error('Payment not found');
        }

        completePayment(payment);
      } else if (paymentIntent.status === 'requires_payment_method') {
        logException(`Payment failed with requires_payment_method status PID ${paymentIntent.id}`);
        setState(checkoutStates.ERROR);
        storage.session.removeItem('CheckoutPage.pid');

        if (router.search.has('payment_intent')) {
          router.search.delete('payment_intent');
          router.search.delete('payment_intent_client_secret');
          router.search.delete('redirect_status');
          router.replace(router.pathname + '?' + router.search.toString());
        }
      } else {
        // Minified frame names change every deploy, so group on the status instead of the stack.
        logException({
          message: `Payment is pending (${paymentIntent.status})`,
          fingerprint: ['payment-intent-pending', paymentIntent.status],
          tags: {
            payment_intent_status: paymentIntent.status,
            payment_intent_next_action: paymentIntent.next_action?.type,
          },
          extra: {
            paymentIntentId: paymentIntent.id,
            paymentMethodTypes: paymentIntent.payment_method_types,
          },
        });
        setState(checkoutStates.PENDING);
        storage.session.removeItem('CheckoutPage.pid');
      }
    } catch (error) {
      logException(error.message);
      setFormError(error.message);
      setState(checkoutStates.ERROR);

      storage.session.removeItem('CheckoutPage.pid');

      const queryParams = new URLSearchParams(router.search);

      if (queryParams.has('payment_intent')) {
        queryParams.delete('payment_intent');
        queryParams.delete('payment_intent_client_secret');
        queryParams.delete('redirect_status');
        router.replace(router.pathname + '?' + queryParams.toString());
      }

      if (payment?._id) {
        await api.setPaymentError(error.message, payment._id);
      }
    }
  }

  return {
    createSubmission,
    createPayment,
    createSubscription,
    processPaymentIntent,
  };
}

export default useCheckoutHelpers;
